import { useState, useEffect, useRef, useCallback } from 'react';
import { Search, LayoutGrid, UserCircle, BarChart3, Activity } from 'lucide-react';
import { useTranslation } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import { Tooltip } from '../ui';
import { getProfilePhoto, getFileUrl } from '../../services/filesService';

// Fixed dock apps (same as Home.jsx)
const DOCK_APPS = [
    { id: 'dashboard', icon: BarChart3, color: '#8b5cf6', navId: 'dashboard' },
    { id: 'modules', icon: LayoutGrid, color: '#6366f1', navId: 'modules' },
    { id: 'search', icon: Search, color: '#f59e0b', navId: 'search' },
    { id: 'profile', icon: UserCircle, color: '#3b82f6', navId: 'profile' },
    { id: 'activities', icon: Activity, color: '#10b981', navId: 'activities' }
];

const DOCK_POSITION_KEY = 'cabaza_dock_position';

// Size configs
const SIZE_CONFIG = {
    large: {
        icon: 'w-10 h-10 sm:w-12 sm:h-12',
        iconInner: 'w-5 h-5 sm:w-6 sm:h-6',
        gap: 'gap-1 sm:gap-3',
        padding: 'p-2 sm:p-3',
        buttonPad: 'p-1 sm:p-2',
        rounded: 'rounded-2xl sm:rounded-3xl',
        buttonRounded: 'rounded-xl sm:rounded-2xl',
        iconRounded: 'rounded-xl sm:rounded-2xl'
    },
    medium: {
        icon: 'w-8 h-8 sm:w-10 sm:h-10',
        iconInner: 'w-4 h-4 sm:w-5 sm:h-5',
        gap: 'gap-1 sm:gap-2',
        padding: 'p-1.5 sm:p-2',
        buttonPad: 'p-1 sm:p-1.5',
        rounded: 'rounded-xl sm:rounded-2xl',
        buttonRounded: 'rounded-lg sm:rounded-xl',
        iconRounded: 'rounded-lg sm:rounded-xl'
    },
    small: {
        icon: 'w-7 h-7 sm:w-8 sm:h-8',
        iconInner: 'w-3.5 h-3.5 sm:w-4 sm:h-4',
        gap: 'gap-0.5 sm:gap-1',
        padding: 'p-1 sm:p-1.5',
        buttonPad: 'p-0.5 sm:p-1',
        rounded: 'rounded-xl sm:rounded-2xl',
        buttonRounded: 'rounded-lg sm:rounded-xl',
        iconRounded: 'rounded-lg sm:rounded-xl'
    }
};

/**
 * DockBar - Floating dock bar at the bottom of all pages
 * Uses theme colors matching the sidebar, draggable with bounds
 */
const DockBar = ({ currentPage, onNavigate, size = 'large', visible = true, dockPosition = 'bottom', isSidebarEnabled = false }) => {
    const { t } = useTranslation();
    const { currentTheme } = useTheme();
    const dockRef = useRef(null);
    const dragState = useRef({ isDragging: false, startX: 0, startY: 0, startLeft: 0, startTop: 0 });
    const [profilePhotoUrl, setProfilePhotoUrl] = useState(null);

    // Position: null = centered at top/bottom (default based on dockPosition prop)
    const [position, setPosition] = useState(() => {
        try {
            const stored = localStorage.getItem(DOCK_POSITION_KEY);
            if (stored) return JSON.parse(stored);
        } catch (e) { /* ignore */ }
        return null;
    });
    const [isDragging, setIsDragging] = useState(false);
    const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 640);

    // Track desktop/mobile for sidebar offset calculation
    useEffect(() => {
        const handleResize = () => setIsDesktop(window.innerWidth >= 640);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const sizeConfig = SIZE_CONFIG[size] || SIZE_CONFIG.large;

    // Load profile photo
    useEffect(() => {
        const loadProfilePhoto = async () => {
            try {
                const userUuid = localStorage.getItem('userUuid');
                if (userUuid) {
                    const result = await getProfilePhoto(userUuid);
                    const success = result.success ?? result.SUCCESS;
                    const data = result.data ?? result.DATA;
                    if (success && data) {
                        const filePath = data.filePath ?? data.FILEPATH ?? data.filepath;
                        if (filePath) {
                            setProfilePhotoUrl(getFileUrl(filePath));
                        }
                    }
                }
            } catch (err) {
                console.error('Error loading profile photo:', err);
            }
        };
        loadProfilePhoto();
    }, []);

    // Save position to localStorage
    useEffect(() => {
        if (position) {
            localStorage.setItem(DOCK_POSITION_KEY, JSON.stringify(position));
        }
    }, [position]);

    // Reset custom position when dockPosition preference changes
    useEffect(() => {
        setPosition(null);
        localStorage.removeItem(DOCK_POSITION_KEY);
    }, [dockPosition]);

    // Constrain position to window bounds
    const constrainPosition = useCallback((x, y) => {
        const dock = dockRef.current;
        if (!dock) return { x, y };
        const rect = dock.getBoundingClientRect();
        const maxX = window.innerWidth - rect.width;
        const maxY = window.innerHeight - rect.height;
        return {
            x: Math.max(0, Math.min(x, maxX)),
            y: Math.max(0, Math.min(y, maxY))
        };
    }, []);

    // Mouse/touch move handler
    const handleMove = useCallback((clientX, clientY) => {
        const ds = dragState.current;
        if (!ds.isDragging) return;
        const dx = clientX - ds.startX;
        const dy = clientY - ds.startY;
        const newPos = constrainPosition(ds.startLeft + dx, ds.startTop + dy);
        setPosition(newPos);
    }, [constrainPosition]);

    const handleEnd = useCallback(() => {
        dragState.current.isDragging = false;
        setIsDragging(false);
    }, []);

    // Mouse events
    useEffect(() => {
        const onMouseMove = (e) => handleMove(e.clientX, e.clientY);
        const onMouseUp = () => handleEnd();
        if (isDragging) {
            window.addEventListener('mousemove', onMouseMove);
            window.addEventListener('mouseup', onMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };
    }, [isDragging, handleMove, handleEnd]);

    // Touch events
    useEffect(() => {
        const onTouchMove = (e) => {
            if (dragState.current.isDragging && e.touches[0]) {
                e.preventDefault();
                handleMove(e.touches[0].clientX, e.touches[0].clientY);
            }
        };
        const onTouchEnd = () => handleEnd();
        if (isDragging) {
            window.addEventListener('touchmove', onTouchMove, { passive: false });
            window.addEventListener('touchend', onTouchEnd);
        }
        return () => {
            window.removeEventListener('touchmove', onTouchMove);
            window.removeEventListener('touchend', onTouchEnd);
        };
    }, [isDragging, handleMove, handleEnd]);

    // Recalculate on resize to keep in bounds
    useEffect(() => {
        const handleResize = () => {
            if (position) {
                setPosition(prev => prev ? constrainPosition(prev.x, prev.y) : null);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [position, constrainPosition]);

    const startDrag = (clientX, clientY) => {
        const dock = dockRef.current;
        if (!dock) return;
        const rect = dock.getBoundingClientRect();
        // If no custom position yet, use current rendered position
        const currentLeft = position ? position.x : rect.left;
        const currentTop = position ? position.y : rect.top;
        dragState.current = {
            isDragging: true,
            startX: clientX,
            startY: clientY,
            startLeft: currentLeft,
            startTop: currentTop
        };
        setIsDragging(true);
    };

    const handleMouseDown = (e) => {
        // Only start drag on the dock background, not on buttons
        if (e.target.closest('button')) return;
        e.preventDefault();
        startDrag(e.clientX, e.clientY);
    };

    const handleTouchStart = (e) => {
        if (e.target.closest('button')) return;
        if (e.touches[0]) {
            startDrag(e.touches[0].clientX, e.touches[0].clientY);
        }
    };

    if (!visible) return null;

    const getDockAppLabel = (appId) => {
        const labels = {
            dashboard: t.menu?.dashboard || 'Dashboard',
            profile: t.menu?.profile || 'Profile',
            activities: t.menu?.activities || 'Activités',
            search: t.menu?.search || 'Search',
            modules: t.menu?.modules || 'Modules'
        };
        return labels[appId] || appId;
    };

    const hasImageBg = currentTheme?.sidebarBgImage;

    const dockStyle = hasImageBg ? {
        backgroundImage: `url(${currentTheme.sidebarBgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
    } : {};

    // Container style: positioned or centered at top/bottom
    const containerStyle = position
        ? { position: 'fixed', left: position.x, top: position.y, zIndex: 99, transform: 'none' }
        : {};

    const defaultPositionClass = dockPosition === 'top' ? 'top-4' : 'bottom-4';
    // When sidebar is enabled (desktop), offset center by sidebar width (80px / 2 = 40px)
    const containerClass = position
        ? '' // absolute positioning via style
        : `fixed ${defaultPositionClass} z-[99]`;

    // Default centered style: account for sidebar on desktop (sm+)
    const sidebarOffset = isSidebarEnabled && isDesktop;
    const defaultCenterStyle = position
        ? {}
        : sidebarOffset
            ? { left: 'calc(80px + (100vw - 80px) / 2)', transform: 'translateX(-50%)' }
            : { left: '50%', transform: 'translateX(-50%)' };

    return (
        <div
            ref={dockRef}
            className={containerClass}
            style={{ ...defaultCenterStyle, ...containerStyle }}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
        >
            <div
                className={`relative overflow-hidden ${sizeConfig.rounded} ${sizeConfig.padding} shadow-2xl ring-2 ring-white/20 backdrop-blur-xl w-fit ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
                style={dockStyle}
            >
                {/* Background: gradient or image overlay (same as sidebar) */}
                {hasImageBg ? (
                    <div
                        className="absolute inset-0 z-0"
                        style={{ backgroundColor: currentTheme.sidebarBgOverlay || 'rgba(0,0,0,0.4)' }}
                    />
                ) : (
                    <div className="absolute inset-0 z-0 bg-gradient-to-r from-cabaza-purple to-cabaza-pink" />
                )}

                <div className={`relative z-10 flex justify-center items-center ${sizeConfig.gap}`}>
                    {DOCK_APPS.map((app) => {
                        const Icon = app.icon;
                        const isActive = currentPage === app.navId;
                        const isProfile = app.id === 'profile';
                        return (
                            <Tooltip key={app.id} text={getDockAppLabel(app.id)} position={dockPosition === 'top' ? 'bottom' : 'top'}>
                                <button
                                    onClick={() => onNavigate(app.navId)}
                                    className={`flex flex-col items-center ${sizeConfig.buttonPad} ${sizeConfig.buttonRounded} hover:bg-white/10 active:scale-90 transition-all ${isActive ? 'bg-white/15' : ''}`}
                                >
                                    {isProfile && profilePhotoUrl ? (
                                        <img
                                            src={profilePhotoUrl}
                                            alt="Profile"
                                            className={`${sizeConfig.icon} rounded-full object-cover shadow-lg transition-transform ${isActive ? 'scale-110 ring-2 ring-white' : 'ring-2 ring-white/50'}`}
                                        />
                                    ) : (
                                        <div
                                            className={`${sizeConfig.icon} ${sizeConfig.iconRounded} shadow-lg flex items-center justify-center transition-transform ${isActive ? 'scale-110' : ''}`}
                                            style={{ backgroundColor: app.color }}
                                        >
                                            <Icon className={`${sizeConfig.iconInner} text-white`} />
                                        </div>
                                    )}
                                </button>
                            </Tooltip>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default DockBar;
